/**
 * TemplateSuggestor Component
 * Phase 5: Session Management UI (2026-01-19)
 *
 * Suggests appropriate WhatsApp templates based on session state
 * and conversation context. Shows session awareness indicators.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Divider,
  CircularProgress,
  Badge
} from '@mui/material';
import {
  AccessTime,
  Warning,
  CheckCircle,
  Send,
  ExpandMore,
  ExpandLess,
  Timer,
  Message,
  Schedule,
  Refresh
} from '@mui/icons-material';
import api from '../../services/api';

interface WhatsAppTemplate {
  id: number;
  name: string;
  category: string;
  language_code: string;
  components: {
    header?: any;
    body?: string;
    footer?: string;
    buttons?: any[];
  };
  variables: string[];
  approval_status: string;
  quality_score?: string;
}

interface SessionState {
  state: 'ACTIVE_24H' | 'TEMPLATE_ONLY' | 'NO_RESPONSE' | 'CLOSED';
  expires_at?: string;
  last_user_message_at?: string;
  hours_remaining?: number;
}

interface TemplateSuggestorProps {
  conversationId: number;
  contactPhone?: string;
  contact?: { id: number; name?: string; email?: string; phone_number?: string; custom_attributes?: Record<string, unknown>; city?: string; country?: string };
  onSelectTemplate: (template: WhatsAppTemplate, variables: Record<string, string>) => void;
  /** Send the currently selected template (human agent / template-only window). */
  onSendTemplate?: () => void | Promise<void>;
  compact?: boolean;
  /** When this value changes, session state and template list are re-fetched (e.g. after sending a message). */
  refreshTrigger?: number;
}

const TemplateSuggestor: React.FC<TemplateSuggestorProps> = ({
  conversationId,
  contactPhone,
  contact,
  onSelectTemplate,
  onSendTemplate,
  compact = false,
  refreshTrigger
}) => {
  const [sessionState, setSessionState] = useState<SessionState | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [suggestedTemplates, setSuggestedTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);

  // Filter suggested templates based on session state (used by fetchData)
  const filterSuggestedTemplatesRef = (
    allTemplates: WhatsAppTemplate[],
    session: SessionState | null
  ): WhatsAppTemplate[] => {
    if (!session) return allTemplates.slice(0, 3);
    if (session.state === 'ACTIVE_24H') {
      return allTemplates.filter(t => t.category === 'UTILITY').slice(0, 3);
    }
    if (session.state === 'TEMPLATE_ONLY') {
      const marketing = allTemplates.filter(t => t.category === 'MARKETING');
      const utility = allTemplates.filter(t => t.category === 'UTILITY');
      return [...marketing, ...utility].slice(0, 5);
    }
    return allTemplates.slice(0, 3);
  };

  const fetchData = React.useCallback(async () => {
    if (!conversationId) return;
    try {
      setLoading(true);
      const cacheBust = { params: { _: Date.now() } };
      const sessionResponse = await api.get(`/whatsapp/sessions/${conversationId}`, cacheBust);
      setSessionState(sessionResponse.data);
      // Cache-bust so browser/proxy never return stale template list (e.g. after adding/syncing templates)
      const templatesResponse = await api.get('/campaigns/whatsapp-templates/', cacheBust);
      const allTemplates = templatesResponse.data.templates || [];
      setTemplates(allTemplates);
      const suggested = filterSuggestedTemplatesRef(allTemplates, sessionResponse.data);
      setSuggestedTemplates(suggested);
    } catch (error) {
      console.error('Error fetching template data:', error);
      setSessionState({ state: 'TEMPLATE_ONLY' });
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  // Fetch session state and templates when conversation or refreshTrigger changes
  useEffect(() => {
    if (conversationId) {
      fetchData();
    }
  }, [conversationId, refreshTrigger, fetchData]);

  // Get session state indicator
  const getSessionIndicator = () => {
    if (!sessionState) return null;

    switch (sessionState.state) {
      case 'ACTIVE_24H':
        return {
          icon: <CheckCircle color="success" />,
          label: 'Active Session',
          color: 'success' as const,
          description: `Session active - ${sessionState.hours_remaining || 0}h remaining`
        };
      case 'TEMPLATE_ONLY':
        return {
          icon: <Warning color="warning" />,
          label: 'Template Only',
          color: 'warning' as const,
          description: 'Outside 24h window - Templates required'
        };
      case 'NO_RESPONSE':
        return {
          icon: <Timer color="info" />,
          label: 'Awaiting Response',
          color: 'info' as const,
          description: 'Waiting for customer reply'
        };
      case 'CLOSED':
        return {
          icon: <AccessTime color="error" />,
          label: 'Session Closed',
          color: 'error' as const,
          description: 'Session expired - Use template to re-engage'
        };
      default:
        return null;
    }
  };

  // const handleSelectTemplate = (template: WhatsAppTemplate) => {
  //   setSelectedTemplate(template);
  //   // Create default variable mappings
  //   const variables: Record<string, string> = {};
  //   //template.variables.forEach((v, index) => {
  //   //  variables[`${index + 1}`] = `{{${v}}}`;
  //   //});
  //   if (template.variables && template.variables.length > 0) {
  //     template.variables.forEach((v, index) => {
  //       variables[`${index + 1}`] = ''; // Empty string, not placeholder
  //     });
  //   }
  //   onSelectTemplate(template, variables);
  // };

  // Added on 09/02/2026 by NK for Dynamic Variables and Media Templates Code Implementation
  const handleSelectTemplate = (template: WhatsAppTemplate) => {
    const variables: Record<string, string> = {};
    if (template.variables && template.variables.length > 0) {
      template.variables.forEach((v, index) => {
        const key = `${index + 1}`;
        if (contact) {
          if (v === 'contact.name' || v === 'name') variables[key] = contact.name ?? '';
          else if (v === 'contact.first_name') variables[key] = (contact.name ?? '').split(/\s+/)[0] ?? '';
          else if (v === 'contact.last_name') variables[key] = (contact.name ?? '').split(/\s+/).slice(1).join(' ') ?? '';
          else if (v === 'contact.email' || v === 'email') variables[key] = contact.email ?? '';
          else if (v === 'contact.phone_number' || v === 'phone_number') variables[key] = contact.phone_number ?? '';
          else if (v === 'contact.company') variables[key] = (contact.custom_attributes as Record<string, string> | undefined)?.company ?? '';
          else if (v === 'contact.city') variables[key] = contact.city ?? '';
          else if (v === 'contact.country') variables[key] = contact.country ?? '';
          else variables[key] = '';
        } else {
          variables[key] = '';
        }
      });
    }
    setSelectedTemplate(template);
    onSelectTemplate(template, variables);
  };
  // End of Added on 09/02/2026 by NK for Dynamic Variables and Media Templates Code Implementation


  const sessionIndicator = getSessionIndicator();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (compact) {
    return (
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {sessionIndicator && (
            <Tooltip title={sessionIndicator.description}>
              <Chip
                icon={sessionIndicator.icon}
                label={sessionIndicator.label}
                size="small"
                color={sessionIndicator.color}
                variant="outlined"
              />
            </Tooltip>
          )}
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          >
            {suggestedTemplates.length} of {templates.length} Templates
          </Button>
          {selectedTemplate && onSendTemplate && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<Send />}
              onClick={() => onSendTemplate()}
            >
              Send Template
            </Button>
          )}
          <Tooltip title="Refresh session and template list">
            <IconButton
              size="small"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); fetchData(); }}
              disabled={loading}
              aria-label="Refresh template list"
            >
              {loading ? <CircularProgress size={20} /> : <Refresh fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {templates.map(template => (
              <Chip
                key={template.id}
                label={template.name}
                size="small"
                onClick={() => handleSelectTemplate(template)}
                color={selectedTemplate?.id === template.id ? 'primary' : 'default'}
                variant={selectedTemplate?.id === template.id ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      {/* Session State Banner */}
      {sessionIndicator && (
        <Alert
          severity={sessionIndicator.color}
          icon={sessionIndicator.icon}
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle2">{sessionIndicator.label}</Typography>
          <Typography variant="caption">{sessionIndicator.description}</Typography>
        </Alert>
      )}

      {/* Template Suggestions Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Suggested Templates
        </Typography>
        <Badge badgeContent={suggestedTemplates.length} color="primary">
          <Message color="action" />
        </Badge>
      </Box>

      {/* Template List */}
      {suggestedTemplates.length > 0 ? (
        <List dense>
          {suggestedTemplates.map((template, index) => (
            <React.Fragment key={template.id}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                button
                selected={selectedTemplate?.id === template.id}
                onClick={() => handleSelectTemplate(template)}
              >
                <ListItemText
                  primary={template.name}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      <Chip
                        label={template.category}
                        size="small"
                        sx={{ fontSize: '0.7rem' }}
                      />
                      <Chip
                        label={template.language_code}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Use this template">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectTemplate(template);
                      }}
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No templates available. Create templates in the Campaign settings.
        </Typography>
      )}

      {/* Session Timer */}
      {sessionState?.expires_at && sessionState.state === 'ACTIVE_24H' && (
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              Session expires: {new Date(sessionState.expires_at).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default TemplateSuggestor;
